import * as React from 'react';

import { Box, Button, TextField } from '@mui/material';

/**
 * Props for CustomCommentSection component
 */
type CustomCommentSectionProps = {
    /** Current custom comment text */
    comment: string;
    /** Whether saving is in progress */
    isSaving: boolean;
    /** Handler for saving the comment */
    onSave: (newComment: string) => void;
    /** Handler for closing the comment */
    onClose?: () => void;
    confirmButtonLabel?: string;
    cancelButtonLabel?: string;
    noCancelButton?: boolean;
};

/**
 * Component for displaying and editing custom comment
 *
 * Shows the comment in readonly mode by default with an edit button.
 * When edit mode is activated, displays a text field and save button.
 */
const CustomCommentSection: React.FunctionComponent<CustomCommentSectionProps> = function ({
    comment,
    isSaving,
    onSave,
    onClose,
    confirmButtonLabel = 'Enregistrer le commentaire',
    cancelButtonLabel = 'Annuler',
    noCancelButton = false,
}) {
    const [localComment, setLocalComment] = React.useState<string>(comment);

    /**
     * Synchronize local state with incoming prop when it changes
     */
    React.useEffect(() => {
        setLocalComment(comment);
    }, [comment]);

    /**
     * Handle save action
     * Calls parent onSave with new comment text and exits edit mode
     */
    const handleSave = () => {
        onSave(localComment);
    };

    /**
     * Cancel edit mode
     * Reverts to original comment value and exits edit mode
     */
    const handleCancel = () => {
        setLocalComment(comment);

        if (onClose) {
            onClose();
        }
    };

    return (
        <Box sx={{ padding: 2 }}>
            <>
                <TextField
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    value={localComment}
                    onChange={(e) => setLocalComment(e.target.value)}
                    placeholder="Ajouter un commentaire personnalisé pour ce rappel..."
                    disabled={isSaving}
                />
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        marginTop: 2,
                    }}
                >
                    {!noCancelButton ? (
                        <Button
                            variant="outlined"
                            sx={{ marginRight: 1 }}
                            onClick={handleCancel}
                            disabled={isSaving}
                        >
                            {cancelButtonLabel}
                        </Button>
                    ) : null}
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        {confirmButtonLabel}
                    </Button>
                </Box>
            </>
        </Box>
    );
};

export default CustomCommentSection;
